import React, { useCallback, useEffect, useState } from 'react';
import { FaTimes, FaRobot, FaFlask, FaSyncAlt } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import { toolsService } from '../../../services/toolsService';
import { friendlyError } from '../../../services/agentsService';

const ms = (n) => (n == null ? '' : n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`);
const when = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'medium' });
const RETENTION = [1, 3, 7, 14, 30, 90];

/** Cómo llamar a una ejecución en pantalla: «Agente Nora · 9 oct 18:30» o «Prueba del editor · …». */
export const runLabel = (r) => `${r.source === 'agent' ? `agente ${r.agents?.name || r.agent_id}` : 'una prueba del editor'} · ${when(r.created_at)}`;

// Ejecuciones de la herramienta (script 40): cada vez que la usó un agente o alguien la probó, con su estado y duración. Al abrir una,
// el lienzo y la vista de cada paso muestran SUS datos de entrada y salida.
export default function RunsPanel({ toolId, openId, onOpen, onClose }) {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [retention, setRetention] = useState(null);
  const load = useCallback(async () => {
    setState('loading');
    try { setRows(await toolsService.listToolRuns(toolId)); setState('ready'); }
    catch (e) { setError(e.code === 'missing_40' ? e.message : friendlyError(e)); setState('error'); }
  }, [toolId]);
  useEffect(() => { load(); toolsService.runRetention(toolId).then(setRetention); }, [load, toolId]);
  const open = async (r) => {
    setBusy(r.id);
    try { const full = await toolsService.getToolRun(r.id); if (full) onOpen(full); } catch (e) { setError(friendlyError(e)); } finally { setBusy(null); }
  };
  return (
    <aside aria-label="Ejecuciones" className="fixed right-0 top-0 bottom-0 z-40 w-full sm:w-[400px] flex flex-col bg-panel ring-1 ring-line" style={{ boxShadow: 'var(--shadow)' }}>
      <header className="flex items-center gap-2 px-4 h-14 border-b border-line">
        <h2 className="font-display text-[17px] font-bold text-fg flex-1">Ejecuciones</h2>
        <button type="button" onClick={load} aria-label="Actualizar" className="h-8 w-8 grid place-items-center rounded-lg text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaSyncAlt aria-hidden className="h-3 w-3" /></button>
        <button type="button" onClick={onClose} aria-label="Cerrar" className="h-8 w-8 grid place-items-center rounded-lg text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60"><FaTimes aria-hidden className="h-3 w-3" /></button>
      </header>
      {retention !== null && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-line text-[12.5px] text-fg-dim">
          <label htmlFor="run-ret">Guardar ejecuciones</label>
          <select id="run-ret" value={retention} onChange={async (e) => { const d = Number(e.target.value); setRetention(d); try { await toolsService.setRunRetention(toolId, d); } catch (err) { setError(friendlyError(err)); } }}
            className="h-7 rounded-full bg-raised ring-1 ring-line px-2 text-[12.5px] text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
            {RETENTION.map((d) => <option key={d} value={d}>{d === 1 ? '1 día' : `${d} días`}</option>)}
            <option value={0}>No guardar</option>
          </select>
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {state === 'loading' && <div aria-busy="true" className="p-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 skeleton rounded-xl" />)}</div>}
        {state === 'error' && <p role="alert" className="m-4 rounded-xl bg-caution-soft px-3 py-2 text-[13px] text-fg">{error}</p>}
        {state === 'ready' && !rows.length && <p className="m-4 text-[13px] text-fg-dim">Todavía no hay ejecuciones. Aparecen cuando la pruebas o cuando un agente la usa.</p>}
        <ul>
          {rows.map((r) => (
            <li key={r.id} className="border-b border-line">
              <button type="button" onClick={() => open(r)} aria-current={openId === r.id ? 'true' : undefined} disabled={busy === r.id}
                className={`w-full text-left flex gap-3 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-live/60 ${openId === r.id ? 'bg-raised' : 'hover:bg-raised/50'}`}>
                <span className={`mt-0.5 grid place-items-center h-8 w-8 shrink-0 rounded-full ${r.source === 'agent' ? 'bg-live-soft text-live' : 'bg-raised text-fg-dim'}`}>
                  {r.source === 'agent' ? <FaRobot aria-hidden className="h-3.5 w-3.5" /> : <FaFlask aria-hidden className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-[13.5px] font-semibold text-fg">{r.source === 'agent' ? (r.agents?.name || `Agente ${r.agent_id}`) : 'Prueba del editor'}</span>
                    <span className={`ml-auto shrink-0 h-2 w-2 rounded-full ${r.status === 'ok' ? 'bg-live' : 'bg-signal'}`} aria-label={r.status === 'ok' ? 'Funcionó' : 'Falló'} />
                  </span>
                  <span className="block text-[12px] text-fg-dim">{when(r.created_at)} · {ms(r.ms)} · {r.tool_version ? `v${r.tool_version}` : 'borrador'}</span>
                  {r.error && <span className="block text-[12px] text-signal truncate">{r.error}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <p className="px-4 py-3 border-t border-line text-[12px] text-fg-faint">Al abrir una, el lienzo y cada paso muestran sus datos. <Btn size="sm" variant="ghost" className="ml-1" onClick={onClose}>Cerrar</Btn></p>
    </aside>
  );
}
